import type { PostTablesLabels200DataItem } from '@ipfabric/api-client/client/schemas/postTablesLabels200DataItem';
import type { DiscoveryDataEnrichmentLink } from '../discoveryDataEnrichmentLink';
import type { EditableAssignedLabel } from './assignedLabelsTypes';

const SHOW_RULE_TOOLTIP = 'Show the auto-assignment rule';
const SHOW_ROOT_ASSIGNMENT_TOOLTIP = 'Show the root assignment';

export const readTableCell = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === 'object' && 'data' in value) {
    const data = (value as { data?: string | null }).data;
    return typeof data === 'string' ? data : undefined;
  }

  return typeof value === 'string' ? value : undefined;
};

/**
 * Manual assignments never set `parentId`, so the labels table joins the row
 * to itself as the root and `rootRuleId` is that row's own `ruleId`.
 */
export const isAssignedLabelLinkedToRule = (row: {
  rootRuleId?: unknown;
}): boolean => readTableCell(row.rootRuleId) !== undefined;

/**
 * The rule that produced an auto-assigned label. Rows inherited from it carry
 * the same root of the assignment chain, so they resolve to the same rule.
 */
export const readAutoRuleLink = (
  row: PostTablesLabels200DataItem,
): DiscoveryDataEnrichmentLink | undefined => {
  if (readTableCell(row.rootAssignment) !== 'auto') {
    return undefined;
  }

  const ruleId = readTableCell(row.rootRuleId);

  return ruleId === undefined ? undefined : { tab: 'rules', ruleId };
};

export const readActionSourceLink = (
  row: PostTablesLabels200DataItem,
): { link: DiscoveryDataEnrichmentLink; tooltip: string } | undefined => {
  const ruleLink = readAutoRuleLink(row);
  if (ruleLink !== undefined) {
    return { link: ruleLink, tooltip: SHOW_RULE_TOOLTIP };
  }

  if (
    readTableCell(row.assignment) !== 'inherited' ||
    readTableCell(row.rootAssignment) !== 'manual'
  ) {
    return undefined;
  }

  const rootId = readTableCell(row.rootId);

  return rootId === undefined
    ? undefined
    : {
        link: { tab: 'assignedLabels', rootId },
        tooltip: SHOW_ROOT_ASSIGNMENT_TOOLTIP,
      };
};

export const toEditableAssignedLabel = (
  row: PostTablesLabels200DataItem,
): EditableAssignedLabel | null => {
  const assignment = readTableCell(row.assignment);
  const labelId = readTableCell(row.labelId);
  const type = readTableCell(row.type);
  const label = readTableCell(row.label);
  const target = readTableCell(row.target);

  if (
    assignment !== 'manual' ||
    !labelId ||
    !label ||
    !target ||
    (type !== 'device' && type !== 'intL2')
  ) {
    return null;
  }

  return {
    hostname: readTableCell(row.hostname) ?? null,
    label,
    labelId,
    linkedToRule: isAssignedLabelLinkedToRule(row),
    target,
    type,
  };
};
